import { Article, OE, ArticleQuantities, ArticleQuantitiesRequest, channel, WholesalerPart, WholesalerArticle } from "@tm/models"
import { useCostEstimationModuleParameters, useWorkTask, useWorkTaskOrderModuleParameters } from "@tm/context-distribution"
import { useCallback, useEffect } from "react"
import { RecoilState, useRecoilState } from "recoil"
import { isEqual, sortBy } from "lodash"
import { getShowQuantitiesByOeArticle, getShowQuantitiesBySupplierArticle, getShowWholesalerArticleQuantities } from "@bundles/basket"
import {
    isSameOeArticle,
    isSameWholesalerArticle,
    mapOeArticleQuantitiesRequest,
    mapSupplierArticleQuantitiesRequest,
    mapWholesalerArticleQuantitiesRequest,
    mapWholesalerPartQuantitiesRequest,
} from "../../helpers"
import { ArticleQuantitiesState, OeArticleQuantitiesState, WholesalerArticleQuantitiesState } from "../../states"

function useGenericBasketQuantities<TArticle, TArticleDto>(
    articles: TArticle[],
    isEnabled: boolean,
    initialState: (workTaskId: string | undefined) => RecoilState<ArticleQuantities<TArticleDto>[]>,
    mapQuantitiesRequest: (article: TArticle, workTaskId: string) => ArticleQuantitiesRequest<TArticleDto>,
    getShowQuantities: (request: ArticleQuantitiesRequest<TArticleDto>[]) => Promise<ArticleQuantities<TArticleDto>[]>,
    isSameArticle: (a: TArticleDto, b: TArticleDto) => boolean,
    extraKey?: string
) {
    const { workTaskId } = useWorkTask() ?? {}
    const { hasWorkTaskOrder } = useWorkTaskOrderModuleParameters()
    const { hasCostEstimation } = useCostEstimationModuleParameters()
    const key = workTaskId + (extraKey ? `_${extraKey}` : "")
    const [basketQuantities, setBasketQuantities] = useRecoilState<ArticleQuantities<TArticleDto>[]>(initialState(key))

    const loadQuantities = useCallback(
        async (requests: ArticleQuantitiesRequest<TArticleDto>[]) => {
            if (requests.length && (hasWorkTaskOrder || hasCostEstimation)) {
                const response = await getShowQuantities(requests)
                setBasketQuantities((state) => {
                    const newList = [
                        ...state.filter((x) => !requests.some((item) => isSameArticle(item.article, x.article))),
                        ...response.filter((x) => !!x.articleQuantities),
                    ]
                    if (!isEqual(sortBy(newList), sortBy(state))) {
                        return newList
                    }
                    return state
                })
            }
        },
        [getShowQuantities, isSameArticle, setBasketQuantities, hasCostEstimation, hasWorkTaskOrder]
    )

    useEffect(() => {
        if (!workTaskId || !isEnabled) {
            return
        }
        const requests = articles.map((article) => mapQuantitiesRequest(article, workTaskId))
        // prevent requests from being filtered by existing quantities to avoid quantities from being updated in current workflow

        loadQuantities(requests)
    }, [articles, workTaskId, isEnabled])

    const updateBasketQuantities = useCallback(
        async (articles: TArticle[]) => {
            if (!workTaskId) {
                return
            }
            const requests = articles.map((article) => mapQuantitiesRequest(article, workTaskId))

            loadQuantities(requests)
        },
        [loadQuantities, mapQuantitiesRequest, workTaskId]
    )

    // TODO: this should be changed in the future to just check the worktask-basket-state instead of channel messages and the endpoint
    useEffect(() => {
        if (!workTaskId) {
            return
        }

        const unsubscriptions: Array<() => void> = []
        unsubscriptions.push(
            channel("WORKTASK", workTaskId).subscribe("BASKET/ARTICLE_ADDED", () => {
                updateBasketQuantities(articles)
            })
        )
        // this is currently used for removing articles within the addToBasket micros (article details, comparison, offers ,...)
        unsubscriptions.push(
            channel("WORKTASK", workTaskId).subscribe("BASKET/ARTICLE_REMOVED", () => {
                updateBasketQuantities(articles)
            })
        )

        return () => {
            unsubscriptions.forEach((unsub) => unsub())
        }
    }, [articles, updateBasketQuantities, workTaskId])

    return { basketQuantities, updateBasketQuantities }
}

export function useBasketQuantities(articles: Article[], isEnabled: boolean, extraKey?: string) {
    return useGenericBasketQuantities(
        articles,
        isEnabled,
        ArticleQuantitiesState,
        mapSupplierArticleQuantitiesRequest,
        getShowQuantitiesBySupplierArticle,
        isEqual,
        extraKey
    )
}

export function useOeBasketQuantities(articles: OE.OePart[], isEnabled: boolean, extraKey?: string) {
    return useGenericBasketQuantities(
        articles,
        isEnabled,
        OeArticleQuantitiesState,
        mapOeArticleQuantitiesRequest,
        getShowQuantitiesByOeArticle,
        isSameOeArticle,
        extraKey
    )
}

export function useWholesalerPartBasketQuantities(articles: WholesalerPart[], isEnabled: boolean, extraKey?: string) {
    return useGenericBasketQuantities(
        articles,
        isEnabled,
        WholesalerArticleQuantitiesState,
        mapWholesalerPartQuantitiesRequest,
        getShowWholesalerArticleQuantities,
        isSameWholesalerArticle,
        extraKey
    )
}

export function useWholesalerArticleBasketQuantities(articles: WholesalerArticle[], isEnabled: boolean, extraKey?: string) {
    return useGenericBasketQuantities(
        articles,
        isEnabled,
        WholesalerArticleQuantitiesState,
        mapWholesalerArticleQuantitiesRequest,
        getShowWholesalerArticleQuantities,
        isSameWholesalerArticle,
        extraKey
    )
}
